import './DashboardCard.css';

import React, { useContext, useEffect, useState } from 'react';
import { generateClassName } from '../../b2b-common/helpers';
import AccessLevel from '../../b2b-plans/components/AccessLevel';
import Counter from '../../b2b-common/Counter';
import MoreButton from '../../b2b-common/MoreButton';
import { ContentCopy, DeleteOutline, Edit, Lock, LockOpen, MoreVert, Public, PublicOff } from '@mui/icons-material';
import { showNotification } from '../../b2b-common/Notification';
import { B2BContentContext } from '../../layout/Layout';
import { LoginContext } from '../../../App';
import { deleteDashboard } from '../../../services/b2b-reporting-apiServices';
import FieldText from '../../b2b-common/FieldText';
import { DashboardGridConfiguration } from './DashboardMiniCard';
import ConfirmationDialog from '../../b2b-common/ConfirmationDialog';
import useB2BTranslation from '../../b2b-common/hooks/useB2BTranslation';
import { Box } from '@mui/material';
import FieldComponent from '../../b2b-common/FieldComponent';
import useObfuscateDashboardId from '../hooks/useObfuscateDashboardId';
import useRolesAndProductsCheck from '../../b2b-common/hooks/useRolesAndProductsCheck';

const DashboardCard = ({ dashboard, className = null, onAccess, onEdit, onDelete }) => {
    const { setNotification } = useContext(B2BContentContext);
    const { loginData } = useContext(LoginContext);
    const { t } = useB2BTranslation('b2b-reporting');
    const { checkProductsPresence } = useRolesAndProductsCheck();

    const [currentDashboard, setCurrentDashboard] = useState(dashboard);
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

    const { encode } = useObfuscateDashboardId();
    const seed = 18082050;
    const obfuscatedDashboardId = encode(loginData.loggedOrganizationUuid, currentDashboard.dashboardId, seed);

    useEffect(() => {
        if (dashboard !== currentDashboard) setCurrentDashboard(dashboard);
    }, [currentDashboard, dashboard]);

    const handleEditDashboard = () => {
        // console.log('handleEditDashboard: ', currentDashboard);
        onEdit && onEdit(currentDashboard);
    }

    const handleDeleteDashboard = async () => {
        // console.log('handleDeleteDashboard: ', currentDashboard);
        try {
            await deleteDashboard(loginData.accessToken, loginData.loggedOrganizationUuid, currentDashboard.dashboardId);
            showNotification(setNotification, t('Dashboard deleted'), 'success', 3000);
            onDelete && onDelete();
        } catch (error) {
            console.error('Error deleting dashboard:', error);
            showNotification(setNotification, t('Error deleting dashboard') + ': ' + error.message, 'error', 3000);
        }
        setIsConfirmationOpen(false);
    }

    const handleClick = () => {
        onAccess && onAccess(currentDashboard);
    }

    const handleCopyPublicUrl = (event) => {
        event.stopPropagation();
        const publicUrl = `${window.location.origin}/pub/reporting/dashboard/${obfuscatedDashboardId}`;
        navigator.clipboard.writeText(publicUrl).then(() => {
            showNotification(setNotification, t('Public URL copied to clipboard'), 'success', 3000);
        }
        ).catch((error) => {
            console.error('Error copying public URL:', error);
            showNotification(setNotification, t('Error copying public URL') + ': ' + error.message, 'error', 3000);
        });
    }

    return (
        <Box
            className={generateClassName('dashboard-card', '', className)}
            onClick={handleClick}
        >
            <Box width={'80%'} display={'flex'} flexDirection={'column'} gap={1}>
                <Box display={'flex'} flexDirection={'row'} alignItems={'center'} justifyContent={'space-between'}>
                    <FieldText label={t('Name')} labelAlign='left'
                        text={currentDashboard.dashboardName} textAlign='left'
                        className={'dashboard-card-field-name'} />
                    <AccessLevel
                        readOnly={dashboard.readOnly}
                        className={'dashboard-card-access-level'}
                    />
                </Box>
                <Box display={'flex'} flexDirection={'row'} alignItems={'center'} justifyContent={'space-between'}>
                    <FieldText label={t('Description')} labelAlign='left'
                        text={currentDashboard.dashboardDescription} textAlign='left' textWordsLimit={20}
                        className={'dashboard-card-field-description'} />
                    {checkProductsPresence(['B2B_CUSTOM_DASHBOARDS_PUBLIC']) && (
                        <FieldComponent label={t('Public Dashboard')} labelAlign='center'
                            component={
                                <Box>
                                    {currentDashboard.publicDashboard ? (
                                        <Box display={'flex'} flexDirection={'row'} alignItems={'center'} justifyContent={'center'} gap={1}>
                                            <Public />
                                            {(currentDashboard.publicDashboard && currentDashboard.dashboardPassword !== null) ?
                                                <Lock /> : <LockOpen />
                                            }
                                        </Box>
                                    ) : <PublicOff />}
                                </Box>
                            }
                            className={'dashboard-card-field-public'} />
                    )}
                </Box>
            </Box>
            <Box width={'20%'} display={'flex'} flexDirection={'column'} alignItems={'center'} justifyContent={'center'} gap={1}>
                <DashboardGridConfiguration dashboard={currentDashboard} readOnly={true} className={'dashboard-card-grid-configuration'} />
                <Counter label={t('Widgets')} count={currentDashboard.widgets.length} minDigits={2} className={'dashboard-card-widgets-counter'} />
            </Box>
            <MoreButton
                icon={<MoreVert />}
                buttons={[
                    ...((currentDashboard.publicDashboard) ? [{
                        label: t('Copy public URL'),
                        icon: <ContentCopy />,
                        rolesAllowed: [],
                        b2bProducts: ['B2B_CUSTOM_DASHBOARDS_PUBLIC'],
                        confirm: false,
                        onClick: handleCopyPublicUrl,
                    }] : []),
                    {
                        label: t('Edit'),
                        icon: <Edit />,
                        rolesAllowed: ['ADMIN', 'EDITOR'],
                        confirm: false,
                        onClick: handleEditDashboard,
                    },
                    {
                        label: t('Delete'),
                        icon: <DeleteOutline />,
                        rolesAllowed: ['ADMIN', 'EDITOR'],
                        onClick: () => setIsConfirmationOpen(true)
                    },
                ]}
                rolesAllowed={['ADMIN', 'EDITOR']}
                className={'dashboard-card-more-button jb01-more-button'}
            />
            <ConfirmationDialog
                title={t('Dashboard Delete Confirmation Request')}
                mainText={t('Are you sure you want to delete the selected dashboard?')}
                open={isConfirmationOpen}
                onClose={() => setIsConfirmationOpen(false)}
                onConfirm={handleDeleteDashboard}
            />
        </Box >
    );
};

export default DashboardCard;